import { ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import userManager from '@/hooks/oidcConfig';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string;
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const whiteList = ['/callback']

  useEffect(() => {
    const checkAuthentication = async () => {
      // 如果在白名单页面，直接返回
      if (whiteList.includes(location.pathname)) {
        return;
      }

      // 未登录且没有token时重定向到登录
      if (!isLoading && !isAuthenticated) {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
          await userManager.signinRedirect({ 
            state: location.pathname || window.location.href 
          });
        }
      }
    };

    checkAuthentication();
  }, [isLoading, isAuthenticated, location.pathname]);

  // 显示加载状态
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
          <p className="text-slate-600">正在验证用户身份...</p>
        </div>
      </div>
    );
  }

  // 未认证用户重定向到登录页
  if (!isAuthenticated) {
    return null; // 不需要重定向，useEffect 会处理
  }

  // 检查角色权限（可选；无 user.role 时不按角色拦截）
  if (requiredRole && user?.role != null && user.role !== requiredRole && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <span className="text-2xl">🚫</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800">权限不足</h2>
          <p className="text-slate-600">您没有访问此页面的权限</p>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回上一页
          </button>
        </div>
      </div>
    );
  }

  // 如果有token，直接显示受保护的内容
  return <>{children}</>;
} 